import { useState } from 'react';
import { Heading, Flex, Box, List, ListItemIcon, ListItem, Dropdown } from '@vibe/core';
import { AppModal, AppModalContent } from '@gorilla/ui/src/components/AppModal';
import { dropdownValue } from '@gorilla/ui/src/components/dropdownValue';
import { DropdownChevronRight, Settings, Table, Workspace, Status, SettingsKnobs, Item, Board, Translation } from '@vibe/icons';

type HelpModalProps = {
  show: boolean;
  onClose: () => void;
  category: string;
  content: string;
};

type HelpListItemProps = {
  opt: {
    label: string;
    value: string;
    image?: string;
    text?: string;
    leftIcon?: any;
  };
  selected?: boolean;
  onClick?: () => void;
};

type HelpContentProps = {
  label: string;
  value: string;
  image?: string;
  text?: string;
  link?: string;
};

const helpOptions = [
  {
    label: 'General settings',
    value: 'general_settings',
    leftIcon: Settings,
    content: [
      {
        label: 'Widget margin',
        value: 'widget_margin',
        image: '/documentation/widget_margin.webp',
        text: 'The widget margin is an optional space around the widget content. Setting it to none is recommended, if you want to embed the widget seamlessly into a website.',
      },
      {
        label: 'Primary color',
        value: 'primary_color',
        image: '/documentation/primary_color.webp',
        text: 'The primary color is being used for regular text in your widget. Use the color picker or enter your preferred value manually, in case you have it at your hands.',
      },
      {
        label: 'Secondary color',
        value: 'secondary_color',
        image: '/documentation/secondary_color.webp',
        text: 'The secondary color is being used for headlines, hover or active states in your widget. Use the color picker or enter your preferred value manually, in case you have it at your hands.',
      },
      {
        label: 'Border color',
        value: 'border_color',
        image: '/documentation/border_color.webp',
        text: 'The border color is being used on any borders in your widget. Use the color picker or enter your preferred value manually, in case you have it at your hands.',
      },
      {
        label: 'Background color',
        value: 'background_color',
        image: '/documentation/background_color.webp',
        text: 'The background color is being used for the widget`s background. Use the color picker or enter your preferred value manually, in case you have it at your hands.',
      },
      {
        label: 'Font family',
        value: 'font_family',
        image: '/documentation/font_family.webp',
        text: 'The font family is being used for the widget`s text. Use the font picker or enter your preferred value manually, in case you have it at your hands.',
      },
      {
        label: 'Font size',
        value: 'font_size',
        image: '/documentation/font_size.webp',
        text: 'The font size is being used for the widget`s text. Use the font size picker or enter your preferred value manually, in case you have it at your hands.',
      },
      {
        label: 'Text alignment',
        value: 'text_alignment',
        image: '/documentation/font_family.webp',
        text: 'The text alignment is being applied to all text in your widget across all layouts. You can make your text aligned to the left, center and right. It is left by default.',
      },
      {
        label: 'Sort column',
        value: 'sort_column',
        image: '/documentation/sort.webp',
        text: 'The sort column is being used to determine which column is sorted by default in your widget. Use the column picker or enter your preferred value manually, in case you have it at your hands.',
      },
      {
        label: 'Sort direction',
        value: 'sort_direction',
        image: '/documentation/sort.webp',
        text: 'The sort direction is being used to determine the order of the sorted column in your widget. Use the direction picker or enter your preferred value manually, in case you have it at your hands.',
      },
    ],
  },
  {
    label: 'Table settings',
    value: 'table_settings',
    leftIcon: Table,
    content: [
      {
        label: 'Show search',
        value: 'show_search',
        image: '/documentation/show_search.webp',
        text: "The show search option toggles a search bar above your table widget. It is only available in the table layout. It's recommended to make use of this option, if you want to make your table content searchable.",
      },
      {
        label: 'Cell margin',
        value: 'cell_margin',
        image: '/documentation/cell_margin.webp',
        text: 'The cell margin defines the inner space of your table cells. It is only available in the table layout. The smaller the cell margin, the more compact your table is being displayed.',
      },
      {
        label: 'Table radius',
        value: 'table_radius',
        image: '/documentation/table_radius.webp',
        text: 'The table radius defines the outer corner radius of your table. It is only available in the table layout. The bigger the table radius, the more rounded your table is being displayed.',
      },
    ],
  },
  {
    label: 'Grid settings',
    value: 'grid_settings',
    leftIcon: Workspace,
    content: [
      {
        label: 'Item margin',
        value: 'item_margin',
        image: '/documentation/item_margin.webp',
        text: 'The item margin defines the inner space of each grid item. It is only available in the grid layout. The smaller the item margin, the more compact your grid is being displayed.',
      },
      {
        label: 'Item gap',
        value: 'item_gap',
        image: '/documentation/item_gap.webp',
        text: 'The item gap defines the space between each grid item. It is only available in the grid layout. The smaller the item gap, the more compact your grid is being displayed.',
      },
      {
        label: 'Item radius',
        value: 'item_radius',
        image: '/documentation/item_radius.webp',
        text: 'The item radius defines the outer corner radius of each grid item. It is only available in the grid layout. The bigger the item radius, the more rounded your items are being displayed.',
      },
      {
        label: 'Item image column',
        value: 'item_image_column',
        image: '/documentation/item_image_column.webp',
        text: "The item image column shows all image columns of your monday.com board. The first image of this column is being used as main image for each grid item. Set it to none if you don't want to use images at all.",
      },
      {
        label: 'Item image format',
        value: 'item_image_format',
        image: '/documentation/item_image_format.webp',
        text: 'The item image format defines the display format of images in your grid items. It is only available in the grid layout. Choose between cover and contain to control how images are displayed.',
      },
      {
        label: 'Item image mode',
        value: 'item_image_mode',
        text: 'The item image mode defines how the main image is displayed in your grid items. It is only available in the grid layout. Choose between thumbnail and gallery to control how images are displayed.',
      },
      {
        label: 'Item headline column',
        value: 'item_headline_column',
        image: '/documentation/item_headline_column.webp',
        text: "The item headline column shows all text columns of your monday.com board. The first text of this column is being used as main headline for each grid item. Set it to none if you don't want to use headlines at all.",
      },
    ],
  },
  {
    label: 'Events settings',
    value: 'events_settings',
    leftIcon: Status,
    content: [
      {
        label: 'Item margin',
        value: 'item_margin',
        text: 'The item margin defines the inner space of each event item. It is only available in the events layout. The smaller the item margin, the more compact your events are being displayed.',
      },
      {
        label: 'Item gap',
        value: 'item_gap',
        text: 'The item gap defines the space between each event item. It is only available in the events layout. The smaller the item gap, the more compact your events are being displayed.',
      },
      {
        label: 'Item radius',
        value: 'item_radius',
        text: 'The item radius defines the outer corner radius of each event item. It is only available in the events layout. The bigger the item radius, the more rounded your items are being displayed.',
      },
      {
        label: 'Item image column',
        value: 'item_image_column',
        text: "The item image column shows all image columns of your monday.com board. The first image of this column is being used as main image for each event item. Set it to none if you don't want to use images at all.",
      },
      {
        label: 'Item image format',
        value: 'item_image_format',
        text: 'The item image format defines the display format of images in your event items. It is only available in the events layout. Choose between cover and contain to control how images are displayed.',
      },
      {
        label: 'Item headline column',
        value: 'item_headline_column',
        text: "The item headline column shows all text columns of your monday.com board. The first text of this column is being used as main headline for each event item. Set it to none if you don't want to use headlines at all.",
      },
      {
        label: 'Item date column',
        value: 'item_date_column',
        text: "The item date column shows all date columns of your monday.com board. The first date of this column is being used as main date for each event item. Set it to none if you don't want to use dates at all.",
      },
      {
        label: 'Date format',
        value: 'date_format',
        text: 'The date format defines the display format of dates in your event items. It is only available in the events layout. Choose between different formats to control how dates are displayed.',
      },
      {
        label: 'Time format',
        value: 'time_format',
        text: 'The time format defines the display format of times in your event items. It is only available in the events layout. Choose between different formats to control how times are displayed.',
      },
      {
        label: 'Item location column',
        value: 'item_location_column',
        text: "The item location column shows all location columns of your monday.com board. The first location of this column is being used as main location for each event item. Set it to none if you don't want to use locations at all.",
      },
      {
        label: 'Item description column',
        value: 'item_description_column',
        text: "The item description column shows all description columns of your monday.com board. The first description of this column is being used as main description for each event item. Set it to none if you don't want to use descriptions at all.",
      },
      {
        label: 'Item learn more column',
        value: 'item_learn_more_column',
        text: "The item learn more column shows all learn more columns of your monday.com board. The first learn more of this column is being used as main learn more for each event item. Set it to none if you don't want to use learn more at all.",
      },
    ],
  },
  {
    label: 'Column settings',
    value: 'column_settings',
    leftIcon: Item,
    content: [
      {
        label: 'Column visibility',
        value: 'column_visibility',
        text: 'Show or hide specific columns in your widget using the column settings. Simply toggle the visibility of each column to customize the display according to your needs.',
      },
      {
        label: 'Column order',
        value: 'column_order',
        text: 'Currently you cannot change the order of columns in your widget directly. They are always displayed in the same order as in your monday.com board.',
      },
      {
        label: 'Column options',
        value: 'column_options',
        text: 'Access each column option by clicking the gear icon. By default all columns have the show label and display label inline options, which likely want to be used specifically on grid layouts. Some columns, such as date and file, have additional options that allow you to customize their display in your widget.',
      },
      {
        label: 'Formula columns',
        value: 'formula_columns',
        text: 'Formula columns are currently supported but with technical limitations. To make formulas visible in your widget, please enable them first within the advanced settings.',
      },
      {
        label: 'Mirror columns',
        value: 'mirror_columns',
        text: 'Mirror columns are fully supported in your widget. You can show or hide them like any other column using the column settings. Though their values are being displayed without column specific styles, but as they were plain text.',
      },
    ],
  },
  {
    label: 'Board filters',
    value: 'board_filters',
    leftIcon: Board,
    content: [
      {
        label: 'Rules',
        value: 'rules',
        text: 'Board filters allow you to filter your board items based on specific rules. You can create rules for any column in your monday.com board, enabling you to display only the relevant items in your widget. In addition, you can combine multiple rules using AND/OR logic to create more complex filtering criteria.',
      },
    ],
  },
  {
    label: 'Text labels',
    value: 'text_labels',
    leftIcon: Translation,
    content: [
      {
        label: 'Labels',
        value: 'labels',
        text: 'Customize the text labels used in your widget to better fit your needs. You can change labels for various elements such as search bar, no results found message, and more. This allows you to provide a more personalized experience for your users, specifically for different use cases and languages.',
      },
    ],
  },
  {
    label: 'Advanced settings',
    value: 'advanced_settings',
    leftIcon: SettingsKnobs,
    content: [
      {
        label: 'Enable subitems',
        value: 'enable_subitems',
        text: 'This is an experimental feature that allows you to display subitems within table layouts. Once enabled, a toggle will appear in the first column of your table, allowing users to expand and collapse subitems for each main item. Currently subitem columns cannot be hidden or filtered individually.',
      },
      {
        label: 'Enable formulas',
        value: 'enable_formulas',
        text: "This is an experimental feature that allows you to display formula column values directly from monday.com within your widget. Once enabled, you can make formula columns visible within your column settings, and show them in your widget. This feature may not work accurately in all cases due to technical limitations with the monday.com API. Further, formulas that reference mirror columns don't work.",
      },
      {
        label: 'URL parameters',
        value: 'url_parameters',
        text: 'By manually adding parameters to your embedded widget URL, you can take advanced control over what is shown in your widget and overwrite specific settings.',
        link: 'https://getgorilla.app/products/board-to-website-widget/docs/advanced-features',
      },
    ],
  },
];

export function HelpModal({ show, onClose, category, content }: HelpModalProps) {
  const [showHelpContent, setShowHelpContent] = useState(() => (category === '' ? 'general_settings' : category));
  const [showHelpContentDetails, setShowHelpContentDetails] = useState(() => (content === '' ? 'widget_margin' : content));

  const HelpListItem = ({ opt }: HelpListItemProps) => (
    <ListItem
      key={opt.value}
      size="xxs"
      onClick={() => {
        setShowHelpContentDetails(opt.value);
      }}
      selected={showHelpContentDetails === opt.value}
    >
      {opt.label}
      <div style={{ marginLeft: 'auto' }}>
        <ListItemIcon icon={DropdownChevronRight} margin="end" />
      </div>
    </ListItem>
  );

  const HelpContentDetails = ({ opt }: { opt?: HelpContentProps }) => {
    const content: HelpContentProps | undefined =
      opt ?? (helpOptions.find((o) => o.value === showHelpContent)?.content || []).find((o) => o.value === showHelpContentDetails);

    if (!content) return null;

    return (
      <>
        {content.image ? (
          <div className="Help--ContentMedia">
            <img src={content.image} width={518} height={200} alt={content.label} />
          </div>
        ) : null}
        <div>
          <h4 style={{ marginBottom: '8px', fontWeight: 600 }}>{content.label}</h4>
          {content.text}
          {content.link && (
            <a href={content.link} target="_blank" style={{ display: 'inline-block', marginLeft: '4px', fontSize: 'inherit' }}>
              Learn more
            </a>
          )}
        </div>
      </>
    );
  };

  return (
    <AppModal
      contentSpacing={false}
      alertDialog={true}
      width="800px"
      data-testid="helpModal"
      show={show}
      onClose={() => {
        onClose();
      }}
    >
      <AppModalContent>
        <Box marginBottom="large">
          <Heading type="h3" weight="bold" align="start" style={{ marginBottom: '15px' }}>
            Documentation
          </Heading>
          <Flex gap={32} direction="row" align="start" className="Help">
            <div className="Help--Sidebar" style={{ minHeight: '360px' }}>
              <div style={{ marginBottom: '16px' }}>
                <Dropdown
                  searchable
                  className="CustomDropdown"
                  onOptionSelect={(ev: any) => {
                    const defaults: Record<string, string> = {
                      general_settings: 'widget_margin',
                      table_settings: 'show_search',
                      grid_settings: 'item_margin',
                      events_settings: 'item_margin',
                      column_settings: 'column_visibility',
                      board_filters: 'rules',
                      text_labels: 'labels',
                      advanced_settings: 'enable_subitems',
                    };

                    setShowHelpContent(ev.value);
                    setShowHelpContentDetails(
                      defaults[ev.value] ?? helpOptions.find((o) => o.value === ev.value)?.content?.[0]?.value ?? '',
                    );
                  }}
                  size="medium"
                  options={helpOptions}
                  value={dropdownValue(helpOptions.find((o) => o.value === showHelpContent))}
                  clearable={false}
                />
              </div>
              <List>
                {(helpOptions.find((o) => o.value === showHelpContent)?.content || []).map((opt) => (
                  <HelpListItem opt={opt} key={opt.value} selected={showHelpContentDetails === opt.value} />
                ))}
              </List>
            </div>
            <div className="Help--Content">
              <div className="Help--ContentDescription">
                <HelpContentDetails />
              </div>
            </div>
          </Flex>
        </Box>
      </AppModalContent>
    </AppModal>
  );
}
